import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Lightweight, dismissible "Install app" prompt for desktop & Android Chrome / Edge.
 *
 * - Listens for the `beforeinstallprompt` event the browser fires when the app
 *   becomes installable (HTTPS + manifest + service worker satisfied).
 * - Hides itself if the user has already installed the app or dismissed the
 *   banner this session.
 *
 * iOS Safari does not fire `beforeinstallprompt`, so on iOS we silently render
 * nothing (users install via the Share → Add to Home Screen menu).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("clinico-install-dismissed") === "1") return;
    // Already installed (running in standalone mode)
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* swallow */
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    sessionStorage.setItem("clinico-install-dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-20 right-20 z-50 max-w-sm rounded-xl border border-zinc-200 bg-white shadow-lg p-4 flex items-start gap-3"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-zinc-900 text-white flex items-center justify-center">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="font-heading text-sm font-semibold text-zinc-900">
          Install Clinico Translate
        </p>
        <p className="text-xs text-zinc-600 mt-1">
          Open it like a native app — faster launch, full-screen, no browser bar.
        </p>
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={install}
            data-testid="install-prompt-install-button"
          >
            Install
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-3 text-xs text-zinc-600"
            onClick={dismiss}
            data-testid="install-prompt-dismiss-button"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        aria-label="Close install prompt"
        onClick={dismiss}
        className="text-zinc-400 hover:text-zinc-600"
        data-testid="install-prompt-close-button"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
